import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import {
    Dns as MasterDataIcon, LocationOn as LocationIcon, Business as DeptIcon, Work as JobTitleIcon, Category as CategoryIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, AutoMode as AutomationIcon
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';

// --- MOCK Data Structures ---
const initialData = {
    departments: [
        { id: 1, name: 'Engineering', code: 'ENG', headcount: 150, budgetCode: 'B401' },
        { id: 2, name: 'Human Resources', code: 'HR', headcount: 30, budgetCode: 'B101' },
        { id: 3, name: 'Finance', code: 'FIN', headcount: 45, budgetCode: 'B201' },
        { id: 4, name: 'Marketing & Sales', code: 'MKT', headcount: 90, budgetCode: 'B301' },
    ],
    locations: [
        { id: 1, name: 'Headquarters - New Delhi', code: 'NDH', city: 'New Delhi', country: 'India', timezone: 'IST' },
        { id: 2, name: 'Regional Office - London', code: 'LON', city: 'London', country: 'UK', timezone: 'GMT' },
        { id: 3, name: 'Development Center - San Jose', code: 'SJC', city: 'San Jose', country: 'USA', timezone: 'PST' },
    ],
    jobTitles: [
        { id: 1, title: 'Software Engineer III', band: 'E3', department: 'Engineering', salaryMin: 80000 },
        { id: 2, title: 'HR Business Partner', band: 'M2', department: 'Human Resources', salaryMin: 65000 },
        { id: 3, title: 'Financial Analyst', band: 'A4', department: 'Finance', salaryMin: 50000 },
    ],
};

const dataTypes = [
    { key: 'departments', name: 'Departments', icon: DeptIcon, color: '#D32F2F' },
    { key: 'locations', name: 'Office Locations', icon: LocationIcon, color: '#388E3C' },
    { key: 'jobTitles', name: 'Job Titles/Bands', icon: JobTitleIcon, color: '#1976D2' },
    { key: 'categories', name: 'Asset Categories', icon: CategoryIcon, color: '#FBC02D' },
];

// --- Column Definitions ---
const getColumns = (dataType, handleEdit, handleDelete) => {
    // Common actions column
    const actionColumn = {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
            <Box>
                <Tooltip title="Edit Record">
                    <IconButton size="small" onClick={() => handleEdit(params.row, dataType)} color="primary">
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete Record">
                    <IconButton size="small" onClick={() => handleDelete(params.row.id, dataType)} color="error">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    };

    switch (dataType) {
        case 'departments':
            return [
                { field: 'id', headerName: 'ID', width: 70 },
                { field: 'name', headerName: 'Department Name', flex: 1, minWidth: 150, editable: true },
                { field: 'code', headerName: 'Code', width: 100, editable: true },
                { field: 'headcount', headerName: 'Headcount', type: 'number', width: 120 },
                { field: 'budgetCode', headerName: 'Budget Code', width: 150, editable: true },
                actionColumn,
            ];
        case 'locations':
            return [
                { field: 'id', headerName: 'ID', width: 70 },
                { field: 'name', headerName: 'Location Name', flex: 1, minWidth: 200, editable: true },
                { field: 'code', headerName: 'Code', width: 100, editable: true },
                { field: 'city', headerName: 'City', width: 150, editable: true },
                { field: 'country', headerName: 'Country', width: 150, editable: true },
                actionColumn,
            ];
        case 'jobTitles':
            return [
                { field: 'id', headerName: 'ID', width: 70 },
                { field: 'title', headerName: 'Job Title', flex: 1, minWidth: 200, editable: true },
                { field: 'band', headerName: 'Job Band', width: 100, editable: true },
                { field: 'department', headerName: 'Default Dept', width: 150, editable: true },
                { field: 'salaryMin', headerName: 'Min Salary ($)', type: 'number', width: 140, valueFormatter: ({ value }) => `$${value.toLocaleString()}` },
                actionColumn,
            ];
        case 'categories':
            return [
                { field: 'id', headerName: 'ID', width: 70 },
                { field: 'name', headerName: 'Category Name', flex: 1, minWidth: 200, editable: true },
                { field: 'description', headerName: 'Description', flex: 2, minWidth: 300, editable: true },
                actionColumn,
            ];
        default:
            return [];
    }
};

// --- Main Master Data Management Component ---
const MasterDataManagement = () => {
    const theme = useTheme();
    const [activeType, setActiveType] = useState(dataTypes[0].key);
    const [data, setData] = useState(initialData);
    const [openDialog, setOpenDialog] = useState(false);
    const [editData, setEditData] = useState(null);
    const [editType, setEditType] = useState(null);

    const activeData = data[activeType] || [];
    const activeColor = dataTypes.find(d => d.key === activeType)?.color || theme.palette.primary.main;

    // CRUD Handlers (Placeholder for API integration)
    const handleAdd = (dataType) => {
        setEditData({});
        setEditType(dataType);
        setOpenDialog(true);
    };

    const handleEdit = (record, dataType) => {
        setEditData(record);
        setEditType(dataType);
        setOpenDialog(true);
    };

    const handleSave = (newRecord) => {
        // Simple mock save logic
        setData(prev => ({
            ...prev,
            [editType]: newRecord.id 
                ? prev[editType].map(r => (r.id === newRecord.id ? newRecord : r)) // Edit
                : [...prev[editType], { ...newRecord, id: Date.now() }] // Add
        }));
        setOpenDialog(false);
    };

    const handleDelete = (id, dataType) => {
        if (window.confirm(`Are you sure you want to delete record ID ${id} from ${dataType}?`)) {
            setData(prev => ({
                ...prev,
                [dataType]: prev[dataType].filter(r => r.id !== id)
            }));
        }
    };
    
    // --- Sub-Component for Dialog Forms (Simplified) ---
    const DynamicFormDialog = () => {
        const [formData, setFormData] = useState(editData || {});
        
        const fields = Object.keys(activeData[0] || {}).filter(key => key !== 'id' && key !== 'headcount'); // Exclude generated fields

        const handleFieldChange = (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        };

        return (
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ bgcolor: activeColor, color: 'white', fontWeight: 600 }}>
                    {editData?.id ? `Edit ${editType.replace(/([A-Z])/g, ' $1').toLowerCase()}` : `Add New ${editType.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {fields.map(field => (
                            <Grid item xs={12} key={field}>
                                <TextField
                                    fullWidth
                                    label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                                    name={field}
                                    value={formData[field] || ''}
                                    onChange={handleFieldChange}
                                    margin="normal"
                                    type={field.includes('salary') || field.includes('cost') ? 'number' : 'text'}
                                    variant="outlined"
                                    required={field === 'name' || field === 'title'}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
                    <Button onClick={() => setOpenDialog(false)} color="error" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={() => handleSave(formData)} color="primary" variant="contained" startIcon={<SaveIcon />}>
                        Save Record
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // --- RENDER COMPONENT ---
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header and Actions */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'headerSlide 0.8s ease-out forwards',
                    '@keyframes headerSlide': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <MasterDataIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    Master Data Management
                </Typography>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    startIcon={<AutomationIcon />} 
                    onClick={() => console.log("Open sync settings")}
                    sx={{ fontWeight: 600 }}
                >
                    Sync Settings
                </Button>
            </Box>

            {/* Main Grid Layout: Responsive Split */}
            <Grid container spacing={4}>
                {/* Left Column: Navigation Sidebar */}
                <Grid 
                    item 
                    xs={12} 
                    md={3} 
                    sx={{ 
                        // Animation: Slide-in from left
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: 'navSlideIn 1s ease-out 0.2s forwards',
                        '@keyframes navSlideIn': { 'to': { opacity: 1, transform: 'translateX(0)' } }
                    }}
                >
                    <Paper elevation={6} sx={{ borderRadius: 3, overflow: 'hidden', position: { md: 'sticky' }, top: theme.spacing(10) }}>
                        <Box sx={{ p: 2, bgcolor: activeColor, color: 'white' }}>
                            <Typography variant="h6" fontWeight={700}>Data Sets</Typography>
                        </Box>
                        <Divider />
                        <List component="nav" disablePadding>
                            {dataTypes.map((type) => (
                                <ListItem key={type.key} disablePadding>
                                    <ListItemButton
                                        selected={activeType === type.key}
                                        onClick={() => setActiveType(type.key)}
                                        sx={{ 
                                            py: 1.5,
                                            borderLeft: activeType === type.key ? `5px solid ${type.color}` : 'none',
                                            '&.Mui-selected': { 
                                                bgcolor: theme.palette.grey[100], 
                                                '&:hover': { bgcolor: theme.palette.grey[200] }
                                            },
                                            '&:hover': { 
                                                bgcolor: theme.palette.action.hover,
                                                transform: 'translateX(3px)', // Subtle hover animation
                                                transition: 'transform 0.2s'
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: activeType === type.key ? type.color : theme.palette.text.secondary }}>
                                            <type.icon />
                                        </ListItemIcon>
                                        <ListItemText primary={type.name} primaryTypographyProps={{ fontWeight: 600 }} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Right Column: Dynamic DataGrid */}
                <Grid 
                    item 
                    xs={12} 
                    md={9}
                    // Animation: Slide-in from right
                    sx={{
                        opacity: 0,
                        transform: 'translateX(20px)',
                        animation: 'contentSlideIn 1s ease-out 0.4s forwards',
                        '@keyframes contentSlideIn': { 'to': { opacity: 1, transform: 'translateX(0)' } }
                    }}
                >
                    <Paper elevation={10} sx={{ height: { xs: 500, md: 700 }, borderRadius: 3, p: { xs: 1, md: 3 } }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={700} sx={{ color: activeColor }}>
                                {dataTypes.find(d => d.key === activeType)?.name} Data ({activeData.length} records)
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={<AddIcon />} 
                                onClick={() => handleAdd(activeType)}
                                sx={{ fontWeight: 600 }}
                            >
                                Add New
                            </Button>
                        </Box>
                        
                        <Box sx={{ height: 'calc(100% - 70px)', width: '100%' }}>
                            <DataGrid
                                rows={activeData}
                                columns={getColumns(activeType, handleEdit, handleDelete)}
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 10 } },
                                }}
                                checkboxSelection
                                disableRowSelectionOnClick
                                slots={{
                                    toolbar: GridToolbar,
                                }}
                                sx={{ border: 'none' }}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Dynamic Add/Edit Dialog */}
            {openDialog && <DynamicFormDialog />}
        </Container>
    );
};

export default MasterDataManagement;